import { MutableRefObject, useEffect, useRef } from 'react'

import { DesktopGridItem, ShortcutGroup } from 'shared/types/desktop-grid'

import { reflowItemsForGridResize } from '../utils'

type UseGridResizeReflowOptions = {
  fitCols: number
  fitRows: number
  rows: number
  itemsLength: number
  shortcutGroups: ShortcutGroup[]
  pinnedLayoutKey?: string
  itemsRef: MutableRefObject<DesktopGridItem[]>
  setItems: (items: DesktopGridItem[]) => void
  setRequiredCols: (cols: number | null) => void
  setRequiredRows: (rows: number | null) => void
}

/**
 * Reflows items (debounced) when the grid's fitting dimensions change, and
 * reports whether extra rows are needed for vertical scroll.
 */
export const useGridResizeReflow = ({
  fitCols,
  fitRows,
  rows,
  itemsLength,
  shortcutGroups,
  pinnedLayoutKey,
  itemsRef,
  setItems,
  setRequiredCols,
  setRequiredRows
}: UseGridResizeReflowOptions) => {
  const prevGridSize = useRef({ cols: 0, rows: 0 })

  useEffect(() => {
    const handle = setTimeout(() => {
      const currentItems = itemsRef.current

      if (fitCols <= 0 || rows <= 0) {
        setRequiredCols(null)
        setRequiredRows(null)
        prevGridSize.current = { cols: fitCols, rows }
        return
      }

      const prevCols = prevGridSize.current.cols
      const prevRows = prevGridSize.current.rows

      const result = reflowItemsForGridResize(
        currentItems,
        prevCols,
        prevRows,
        fitCols,
        rows,
        {
          fitRows,
          // Layout mode follows the device class. The grid element's own
          // width can be mid-layout at boot; measuring it here once flipped
          // desktop sessions into sequential mode and packed the icons.
          containerWidth: window.innerWidth,
          groups: shortcutGroups
        }
      )

      const layoutsEqual =
        currentItems.length === result.items.length &&
        currentItems.every((item, index) => {
          const next = result.items[index]
          return (
            item.id === next.id &&
            item.position.row === next.position.row &&
            item.position.col === next.position.col &&
            item.size.width === next.size.width &&
            item.size.height === next.size.height
          )
        })

      if (!layoutsEqual) {
        setItems(result.items)
      }

      setRequiredCols(null)
      if (result.needsVerticalScroll) {
        setRequiredRows(result.requiredRows)
      } else {
        setRequiredRows(null)
      }

      prevGridSize.current = {
        cols: result.requiredCols,
        rows: result.requiredRows
      }
    }, 200)

    return () => {
      clearTimeout(handle)
    }
  }, [
    fitCols,
    fitRows,
    rows,
    itemsLength,
    shortcutGroups,
    pinnedLayoutKey,
    itemsRef,
    setItems,
    setRequiredCols,
    setRequiredRows
  ])
}
